/**
 * Modal component with Panerai dark theme
 * Supports different sizes, header/footer, and keyboard navigation
 */

import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from './Icon'

/**
 * Modal backdrop/overlay component
 */
function ModalOverlay({ onClick, className = '' }) {
  return (
    <div
      className={`fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity ${className}`}
      onClick={onClick}
      aria-hidden="true"
    />
  )
}

/**
 * Main Modal component
 */
export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showClose = true,
  closeOnBackdrop = true,
  closeOnEscape = true,
  footer,
  className = '',
}) {
  const modalRef = useRef(null)
  const previousActiveElement = useRef(null)
  const onCloseRef = useRef(onClose)
  const closeOnEscapeRef = useRef(closeOnEscape)

  // Keep refs current without triggering effect re-runs
  onCloseRef.current = onClose
  closeOnEscapeRef.current = closeOnEscape

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    full: 'max-w-full mx-4',
  }

  // Focus management and body scroll lock — only re-run when isOpen changes
  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement
      modalRef.current?.focus()
      document.body.style.overflow = 'hidden'

      const handleKeyDown = (event) => {
        if (event.key === 'Escape' && closeOnEscapeRef.current) {
          onCloseRef.current()
        }
      }
      document.addEventListener('keydown', handleKeyDown)

      return () => {
        document.body.style.overflow = ''
        document.removeEventListener('keydown', handleKeyDown)
        if (previousActiveElement.current) {
          previousActiveElement.current.focus?.()
        }
      }
    }
  }, [isOpen])

  if (!isOpen) return null

  const handleBackdropClick = () => {
    if (closeOnBackdrop) {
      onClose()
    }
  }

  const modalContent = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      <ModalOverlay onClick={handleBackdropClick} />

      <div
        ref={modalRef}
        tabIndex={-1}
        className={`
          relative bg-dial-surface border border-dial-border rounded-2xl shadow-elevated w-full ${sizeClasses[size]}
          max-h-[90vh] flex flex-col
          animate-scale-in
          ${className}
        `}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {(title || showClose) && (
          <div className="flex items-center justify-between p-4 border-b border-dial-border flex-shrink-0">
            {title && (
              <h2 id="modal-title" className="text-lg font-semibold text-indices-bright font-display">
                {title}
              </h2>
            )}
            {showClose && (
              <button
                onClick={onClose}
                className="p-2 -mr-1 text-indices-muted hover:text-indices-bright hover:bg-dial-elevated rounded-lg transition-colors active:scale-95"
                aria-label="Close modal"
              >
                <Icon name="close" className="w-5 h-5" />
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">{children}</div>

        {/* Footer */}
        {footer && (
          <div className="p-4 border-t border-dial-border flex-shrink-0">{footer}</div>
        )}
      </div>
    </div>
  )

  // Render into portal
  return createPortal(modalContent, document.body)
}

/**
 * Confirmation modal for destructive actions
 */
export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger', // 'danger' | 'warning' | 'primary'
  isLoading = false,
}) {
  const variantClasses = {
    danger: 'bg-status-error text-dial-black hover:bg-status-error/90',
    warning: 'bg-status-warning text-dial-black hover:bg-status-warning/90',
    primary: 'bg-lume-teal text-dial-black hover:bg-lume-green',
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 py-2.5 px-4 bg-dial-elevated text-indices-bright border border-dial-border rounded-xl font-medium hover:bg-dial-surface transition-colors disabled:opacity-50"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`flex-1 py-2.5 px-4 rounded-xl font-medium transition-colors disabled:opacity-50 ${variantClasses[variant]}`}
          >
            {isLoading ? 'Loading...' : confirmText}
          </button>
        </div>
      }
    >
      <p className="text-indices-medium">{message}</p>
    </Modal>
  )
}

/**
 * Alert modal for messages/notifications
 */
export function AlertModal({
  isOpen,
  onClose,
  title,
  message,
  type = 'info', // 'info' | 'success' | 'warning' | 'error'
  buttonText = 'OK',
}) {
  const iconConfig = {
    info: { name: 'informationCircle', color: 'text-status-info' },
    success: { name: 'checkCircle', color: 'text-status-success' },
    warning: { name: 'exclamationTriangle', color: 'text-status-warning' },
    error: { name: 'xCircle', color: 'text-status-error' },
  }

  const config = iconConfig[type]

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" showClose={false}>
      <div className="text-center py-4">
        <div className={`mx-auto w-12 h-12 ${config.color} mb-4`}>
          <Icon name={config.name} className="w-12 h-12" />
        </div>
        {title && (
          <h3 className="text-lg font-semibold text-indices-bright mb-2 font-display">{title}</h3>
        )}
        <p className="text-indices-medium mb-6">{message}</p>
        <button
          onClick={onClose}
          className="w-full py-3 bg-lume-teal text-dial-black rounded-xl font-medium hover:bg-lume-green hover:shadow-lume transition-all"
        >
          {buttonText}
        </button>
      </div>
    </Modal>
  )
}

/**
 * Bottom sheet modal for mobile
 */
export function BottomSheet({
  isOpen,
  onClose,
  title,
  children,
  showHandle = true,
}) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  if (!isOpen) return null

  return createPortal(
    <div className="fixed inset-0 z-50">
      <ModalOverlay onClick={onClose} />
      <div
        className="absolute bottom-0 left-0 right-0 bg-dial-surface border-t border-dial-border rounded-t-2xl max-h-[90vh] flex flex-col animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {showHandle && (
          <div className="flex justify-center py-2">
            <div className="w-10 h-1 bg-dial-border rounded-full" />
          </div>
        )}
        {title && (
          <div className="px-4 py-3 border-b border-dial-border">
            <h2 className="text-lg font-semibold text-indices-bright text-center font-display">
              {title}
            </h2>
          </div>
        )}
        <div className="flex-1 overflow-y-auto p-4">{children}</div>
      </div>
    </div>,
    document.body
  )
}

export default Modal
